/* ============================================================
   LANGKAH CONFIGURE — RUPA v3.9
   ============================================================
   ⭐ DIPAKAI DI DUA TEMPAT (sejak 2026-08-20):
      · /prostudio/agent      — HALAMAN HIDUP, Langkah 4, bila
        suis v3.9 hidup. Data sebenar disuap oleh
        ProStudioAgentSection (Firestore + jambatan extension).
      · /prostudio-v3.9/agent — lakaran, data contoh.

   ⛔ KOMPONEN INI RUPA SAHAJA. Tiada Firestore, tiada extension,
      tiada applyRecipe di dalamnya. Semua logik kekal pada
      pemanggil; fail ini hanya menerima prop dan melukis. Itu
      yang membolehkan lakaran dan halaman hidup berkongsi satu
      takrifan rupa — jangan pindahkan logik masuk ke sini.

   ⛔ RUPA LAMA (ProStudioAgentSection dengan v39 = false) TIDAK
      memakai fail ini langsung. Ia laluan pelanggan sebenar hari
      ini dan markupnya kekal seperti asal.

   ── KENAPA FAIL INI WUJUD ──────────────────────────────────
   Aduan user 2026-08-19: langkah Configure "serabut dan
   crowded" untuk pelanggan baharu. Dikira pada skrin sebenar,
   langkah itu memapar SERENTAK: kotak arahan, baris hijau
   status, pemilih produk, butang, pemilih model Gemini + nota
   kegagalannya, tajuk padanan + lencana + ayat arahan, dua
   jadual, LIMA kotak teks, LIMA baris cip (8 cip setiap satu
   = 40 cip serupa), kad pratonton, dan jawapan mentah.

   Tiga keputusan reka bentuk menjawabnya:

   1. SATU KEADAAN PADA SATU MASA. Belum jalan / sedang jalan /
      sudah siap tidak pernah berkongsi skrin. Arahan "biarkan
      ia berjalan" hilang sebaik ia selesai berjalan — ia sudah
      tiada gunanya di situ.

   2. BACA DAHULU, EDIT BILA PERLU. Setiap medan dipapar
      sebagai SATU baris yang boleh dibaca (nama + blok yang
      dipadankan + tanda betul). Kotak teks dan cip hanya
      muncul untuk baris yang dibuka, dan hanya SATU baris
      boleh terbuka pada satu masa. Ini menurunkan 5 kotak
      teks + 40 cip kepada 0 pada pandangan pertama.

   3. KAWALAN PAKAR DITURUNKAN. Pusingan jawapan bukan keputusan
      harian — ia duduk dalam laci "Tetapan lanjutan" (terbuka secara lalai sejak 2026-09-27).
      ⭐ 2026-08-21 (arahan user): pemilih model Gemini + jawapan
      penuh KELUAR daripada laci itu dan naik ke bawah tajuk
      "Apa yang SESAAT ambil daripada jawapan". Sebabnya: kedua-dua
      benda itu menerangkan jawapan yang SEDANG dipadankan di
      bawahnya — ia sebahagian daripada seksyen itu, bukan tetapan
      pakar yang berasingan.

   ⚠️ Baris yang TIDAK berpadan dibuka sendiri dan bertanda
      jalur amber di tepi kiri. Ia satu-satunya perkara pada
      halaman ini yang benar-benar menuntut tindakan, jadi ia
      satu-satunya yang dibenarkan menjerit.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import {
    Check, ChevronRight, ChevronDown, AlertTriangle, RotateCcw, Play,
    Image as IkonGambar, Video, Type, Loader2, ListRestart, Plus,
} from 'lucide-react';

import '../design/tokens.css';
import { Btn } from '../design/ui';
import { Lbl, Sel } from './prostudioUI';

/* CSS berskop awalan `.psk-`, dalam fail sendiri — corak sama
   HooksV39View. Ia di sini dan bukan gaya inline kerana
   baris senarai perlukan :hover dan susun atur telefon perlukan
   media query; gaya inline tidak boleh pegang kedua-duanya. */
const GAYA = `
.psk { display: grid; gap: 18px; }

/* -- Panel tengah: keadaan "belum jalan" dan "sedang jalan" -- */
.psk-hero { border: 1px solid var(--s-line); border-radius: 12px; padding: 28px 22px; text-align: center; }
.psk-hero__ikon {
  width: 42px; height: 42px; border-radius: 999px; margin: 0 auto 14px;
  display: grid; place-items: center;
  background: var(--s-fill); border: 1px solid var(--s-line); color: var(--s-ink-2);
}
.psk-hero__t { margin: 0 0 7px; font-size: 15px; font-weight: 500; color: var(--s-ink); }
.psk-hero__d { margin: 0 auto; max-width: 400px; font-size: 13px; line-height: 1.65; color: var(--s-ink-3); }
.psk-hero__form { max-width: 400px; margin: 22px auto 0; text-align: left; }
/* Dua butang bila pelanggan datang daripada jalur hijau (kembali + jalankan);
   satu sahaja pada larian pertama. Jurang diletak di sini, bukan pada butang,
   supaya kes satu-butang tidak mewarisi ruang yang tergantung. */
.psk-hero__btn { margin-top: 16px; display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; }

/* -- Senarai langkah larian -- */
.psk-run { display: grid; gap: 11px; max-width: 400px; margin: 0 auto; text-align: left; }
.psk-run__b { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--s-ink-3); }
.psk-run__b--kini { color: var(--s-ink); }
.psk-run__b--siap { color: var(--s-ink-2); }
.psk-run__d {
  width: 18px; height: 18px; flex: none; border-radius: 999px; display: grid; place-items: center;
  border: 1px solid var(--s-line-strong); color: var(--s-ink-3);
}
.psk-run__b--siap .psk-run__d { border-color: transparent; background: var(--s-ok-soft); color: var(--s-ok); }
.psk-run__t { margin-left: auto; font-variant-numeric: tabular-nums; font-size: 12.5px; color: var(--s-ink-3); }
.psk-spin { animation: psk-spin 0.9s linear infinite; }
@keyframes psk-spin { to { transform: rotate(360deg); } }

/* -- Jalur status "sudah sedia" -- */
.psk-status { display: flex; align-items: center; gap: 12px; padding: 13px 15px; border-radius: 10px; background: var(--s-ok-soft); }
.psk-status__i { flex: none; color: var(--s-ok); display: grid; place-items: center; }
.psk-status__c { flex: 1; min-width: 0; }
.psk-status__t { font-size: 13.5px; font-weight: 500; color: var(--s-ink); }
.psk-status__d { margin-top: 2px; font-size: 12.5px; color: var(--s-ink-2); }
/* Butang "Configure semula" DUDUK ATAS jalur hijau, jadi butang telus lenyap
   di situ — ia dibaca sebagai teks, bukan sesuatu yang boleh ditekan (aduan
   user 2026-08-20). Ia diberi permukaan kad sendiri supaya ia menonjol
   daripada latar hijau itu.
   ⛔ SENGAJA BUKAN butang utama hitam. Perkara paling kuat pada skrin ini
   mesti kekal tanda amber "medan ini belum ada sumber" — itu yang menuntut
   tindakan. Ini tindakan pemulihan; ia perlu jelas, bukan paling kuat. */
.psk-status__a {
  background: var(--s-card); border-color: var(--s-line-strong); color: var(--s-ink);
}
.psk-status__a:hover:not(:disabled) { background: var(--s-fill-hover); }

/* -- Padan semula selepas tukar "Output diambil daripada" (2026-09-13) --
   Aduan user: pelanggan menukar pusingan jawapan dan skrin kekal sama selama
   panggilan pemadan AI berjalan — dibaca sebagai "tiada apa berlaku". Jalur
   status bertukar neutral + pusingan + pemasa saat (bukti ia bekerja), dan hasil
   lama di bawahnya dimalapkan + DIKUNCI: suntingan di tengah padanan akan ditimpa
   senyap oleh hasil baharu sebaik ia tiba. */
.psk-status--sibuk { background: var(--s-fill); }
.psk-status--sibuk .psk-status__i { color: var(--s-ink-2); }
.psk-status__s { flex: none; font-variant-numeric: tabular-nums; font-size: 12.5px; color: var(--s-ink-3); }
.psk-sibuk { opacity: .45; pointer-events: none; user-select: none; transition: opacity .2s ease; }
.psk-sedang { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12.5px; color: var(--s-ink); }
.psk-sedang__t { margin-left: auto; font-variant-numeric: tabular-nums; color: var(--s-ink-3); }

/* -- Jalur ralat -- */
.psk-ralat {
  padding: 11px 13px; border-radius: 10px; background: var(--s-stop-soft); color: var(--s-stop);
  font-size: 12.5px; line-height: 1.55;
}

/* -- Tajuk seksyen --
   ⭐ JALUR BERWARNA (arahan user 2026-09-13: "tajuk setiap section tu di warna kan
   kontra seperti bila di hover mouse"). SEMUA tajuk pada skrin hasil — seksyen biasa
   (jalur ini) DAN kepala kotak/laci (.psk-more__h) — memakai latar yang SAMA,
   --s-fill-hover, iaitu warna hover lama. Tajuk yang BOLEH ditekan (laci buka/tutup)
   jadi lebih gelap bila hover (--s-fill-strong); tajuk biasa TIDAK berubah bila hover
   kerana ia tidak boleh ditekan — perubahan hover di situ ialah kawalan palsu.
   ⚠️ Padding + saiz huruf WAJIB sama dengan .psk-more__h supaya kedua-dua jenis tajuk
   dibaca sebagai satu keluarga. Ubah satu, ubah dua-dua. */
.psk-tajuk {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 15px; border-radius: 10px; background: var(--s-fill-hover);
  font-size: 13.5px; font-weight: 500; color: var(--s-ink);
}
/* Ayat penerangan di bawah jalur dijajar dengan TEKS tajuk, bukan dengan tepi jalur. */
.psk-tajuk ~ .psk-h__d { padding: 0 15px; }
.psk-tajuk + .psk-h__d { margin-top: 8px; }
.psk-h__d { margin-top: 4px; font-size: 12.5px; line-height: 1.6; color: var(--s-ink-3); }
.psk-h__d--amaran { color: var(--s-warn); }

/* -- Amaran + butang baca model (laci lanjutan) -- */
.psk-amaran {
  margin-top: 7px; padding: 9px 11px; border-radius: 8px;
  background: var(--s-warn-soft); color: var(--s-warn);
  font-size: 12.5px; line-height: 1.55;
}
.psk-model { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 9px; }
.psk-model__h { flex: 1; min-width: 180px; font-size: 12px; line-height: 1.5; color: var(--s-ink-3); }
.psk-h__d--stop { color: var(--s-stop); }
.psk-pautan { background: none; border: 0; padding: 0; font: inherit; color: var(--s-ink-2); text-decoration: underline; cursor: pointer; }
.psk-pautan:hover { color: var(--s-ink); }

/* Pilihan wajib — dua kotak, bukan suis. Suis ada keadaan LALAI, dan lalai
   itulah yang membuat pilihan senyap bagi pihak pelanggan; dua kotak kosong
   memaksa satu tekanan sebelum larian boleh bermula. */
.psk-pilih { display: grid; gap: 8px; }
.psk-pilih__b {
  width: 100%; box-sizing: border-box; display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px; border-radius: 9px; text-align: left; cursor: pointer;
  background: transparent; border: 1px solid var(--s-line-strong); font-family: inherit;
}
.psk-pilih__b:hover { background: var(--s-fill-hover); }
.psk-pilih__b--on { border-color: var(--s-ink-3); background: var(--s-fill); }
.psk-pilih__r {
  flex: none; width: 17px; height: 17px; margin-top: 1px; border-radius: 999px;
  display: grid; place-items: center;
  border: 1px solid var(--s-line-strong); color: var(--s-select-ink);
}
.psk-pilih__b--on .psk-pilih__r { background: var(--s-select); border-color: var(--s-select); }
.psk-pilih__c { flex: 1; min-width: 0; }
.psk-pilih__t { display: block; font-size: 13.5px; color: var(--s-ink); }
.psk-pilih__d { display: block; margin-top: 3px; font-size: 12px; line-height: 1.5; color: var(--s-ink-3); }
/* -- Blok buatan pelanggan -- */
.psk-inp {
  flex: 1; min-width: 200px; height: 32px; box-sizing: border-box;
  padding: 0 11px; border-radius: 8px; border: 1px solid var(--s-line-strong);
  background: var(--s-card); color: var(--s-ink);
  font-family: var(--s-mono); font-size: 12.5px; outline: none;
}
.psk-inp:focus { border-color: var(--s-ink-3); }
/* Kotak jawapan yang boleh diserlah — teks boleh dipilih, dan ia
   dipendekkan supaya laci tidak menelan seluruh skrin. */
.psk-raw--pilih { user-select: text; cursor: text; max-height: 220px; }
/* Serlahan padanan blok buatan pelanggan. Warna "berjaya", sama keluarga
   dengan kiraan padanan di bawahnya, supaya mata mengaitkan kedua-duanya. */
.psk-mark {
  background: var(--s-ok-soft); color: inherit;
  border-radius: 3px; padding: 0 2px; box-shadow: inset 0 0 0 1px var(--s-ok);
}
.psk-blok { display: flex; align-items: flex-start; gap: 10px; padding: 11px 15px; }
.psk-blok__c { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.psk-blok__k { font-size: 12px; color: var(--s-ok); }
.psk-blok__k--kosong { color: var(--s-warn); }
.psk-blok__k--tak-tahu { color: var(--s-ink-3); }
.psk-blok__v {
  flex: 1 1 100%; font-size: 12px; line-height: 1.5; color: var(--s-ink-3);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* -- Senarai padanan -- */
.psk-list { border: 1px solid var(--s-line); border-radius: 10px; overflow: hidden; }
.psk-grp {
  padding: 9px 15px; background: var(--s-fill); border-top: 1px solid var(--s-line);
  font-size: 11px; letter-spacing: .07em; text-transform: uppercase; color: var(--s-ink-3);
}
/* Baris pemisah di ATAS elemen pertama sekali akan bertindih dengan bingkai
   senarai. Dua pemilih kerana kumpulan boleh bermula dengan tajuknya ATAU
   (bila tiada tajuk) terus dengan baris pertamanya. */
.psk-list > *:first-child > .psk-grp,
.psk-list > *:first-child > .psk-item:first-child { border-top: 0; }
.psk-item { border-top: 1px solid var(--s-line); }
.psk-item--buka { background: var(--s-fill); }
.psk-item--kurang { box-shadow: inset 3px 0 0 var(--s-warn); }

.psk-row {
  width: 100%; box-sizing: border-box; display: flex; align-items: center; gap: 12px;
  padding: 13px 15px; background: transparent; border: 0; text-align: left;
  font-family: inherit; cursor: pointer;
}
.psk-row:hover { background: var(--s-fill-hover); }
/* Amaran bilangan kemunculan blok. Duduk DI BAWAH baris (bukan di dalamnya)
   supaya ayat penuh terbaca — baris itu sendiri satu talian dan memotong teks. */
.psk-nota {
  margin: 0; padding: 0 13px 9px 40px;
  font-size: 12px; line-height: 1.5; color: var(--s-warn);
}
.psk-row__i { flex: none; color: var(--s-ink-3); display: grid; place-items: center; }
.psk-row__n { flex: 0 0 152px; min-width: 0; font-size: 13.5px; color: var(--s-ink); }
/* Nilai dirender sebagai satu ALIRAN teks biasa, bukan flex bercelah:
   petikan dan titik bertindih rapat pada blok seperti dalam ayat sebenar.
   Versi flex pertama meletakkan celah 5px di sekeliling setiap serpihan,
   jadi tanda petikan penutup terapung sendirian jauh dari perkataannya. */
.psk-row__v { flex: 1; min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: 12.5px; color: var(--s-ink-3); }
.psk-row__s { flex: none; display: grid; place-items: center; }
.psk-row__c { flex: none; color: var(--s-ink-3); transition: transform .15s ease; }
.psk-item--buka .psk-row__c { transform: rotate(180deg); }

/* Blok = serpihan jawapan agent. Dipapar sebagai cip supaya pelanggan
   nampak ia satu benda, bukan teks biasa yang tersalah taip. */
.psk-tok {
  display: inline-block; vertical-align: baseline; margin: 0 1px;
  font-family: var(--s-mono); font-size: 11.5px; padding: 2px 8px; border-radius: 999px;
  background: var(--s-card); color: var(--s-ink-2); border: 1px solid var(--s-line-strong);
}
.psk-kosong { font-size: 12.5px; color: var(--s-warn); }

/* -- Editor satu baris -- */
.psk-edit { padding: 0 15px 15px 15px; }
.psk-edit__ta {
  width: 100%; box-sizing: border-box; min-height: 68px; resize: vertical;
  padding: 10px 12px; border: 1px solid var(--s-line-strong); border-radius: 8px;
  background: var(--s-card); color: var(--s-ink);
  font-family: inherit; font-size: 13px; line-height: 1.6; outline: none;
}
.psk-edit__ta:focus { border-color: var(--s-ink-3); }
.psk-pal { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 10px; }
.psk-pal__l { font-size: 12px; color: var(--s-ink-3); margin-right: 2px; }
.psk-pal__b {
  font-family: var(--s-mono); font-size: 11.5px; padding: 4px 9px; border-radius: 999px; cursor: pointer;
  background: var(--s-card); color: var(--s-ink-2); border: 1px solid var(--s-line-strong);
}
.psk-pal__b:hover { background: var(--s-select); color: var(--s-select-ink); border-color: var(--s-select); }
.psk-edit__f { display: flex; align-items: center; gap: 10px; margin-top: 11px; }
.psk-edit__h { flex: 1; min-width: 0; font-size: 12px; line-height: 1.55; color: var(--s-ink-3); }

/* -- Kotak berkepala: laci (Tetapan lanjutan, Blok anda sendiri) + kotak tetap (Model Gemini) --
   Kepala SENTIASA berwarna (lihat nota .psk-tajuk). Laci = butang, jadi ia lebih gelap
   bila hover. Kotak tetap (.psk-more--tetap) = kepala biasa tanpa anak panah: ia sentiasa
   terbuka dan tidak boleh ditutup (arahan user 2026-09-13 — pemilih model penting;
   kotak yang boleh ditutup mudah terlepas).
   ⚠️ Sudut kepala dibundarkan SENDIRI (9px = bingkai 10px tolak garis 1px), BUKAN dengan
   overflow:hidden pada kotak — itu memotong cincin fokus butang kepala. */
.psk-more { border: 1px solid var(--s-line); border-radius: 10px; }
.psk-more__h {
  width: 100%; box-sizing: border-box; display: flex; align-items: center; gap: 10px;
  padding: 13px 15px; background: var(--s-fill-hover); border: 0; border-radius: 9px; text-align: left;
  font-family: inherit; font-size: 13.5px; color: var(--s-ink); cursor: pointer;
}
.psk-more--buka .psk-more__h, .psk-more--tetap .psk-more__h { border-radius: 9px 9px 0 0; }
button.psk-more__h:hover { background: var(--s-fill-strong); }
.psk-more__h--tetap { cursor: default; }
.psk-more__t { font-weight: 500; }
.psk-more__d { font-size: 12.5px; color: var(--s-ink-3); }
.psk-more__c { margin-left: auto; flex: none; color: var(--s-ink-3); transition: transform .15s ease; }
.psk-more--buka .psk-more__c { transform: rotate(90deg); }
/* Ruang atas 14px (dulu 2px): kepala kini berwarna, jadi isi yang rapat 2px di bawahnya
   kelihatan melekat pada jalur itu. */
.psk-more__b { padding: 14px 15px 15px; display: grid; gap: 14px; }

/* -- Kad pratonton — meniru kad produk Workspace --
   Susun atur disalin daripada kad sebenar: kepala (gambar + nama), kemudian
   satu LAJUR setiap set bersebelahan, dipisah garis menegak; dalam setiap
   lajur, cip nama set diikuti lima kotak berlabel. Label = huruf besar kecil
   dengan titik berwarna; nilai dalam kotak berbingkai dengan tinggi tetap dan
   skrol sendiri — sama seperti kotak teks pada kad. */
/* gambar daripada jawapan agent (pratonton) */
.psk-gbr__baris { display: flex; gap: 10px; flex-wrap: wrap; }
.psk-gbr__img { height: 170px; width: auto; max-width: 100%; border-radius: 10px; border: 1px solid var(--s-line); background: var(--s-fill); object-fit: contain; }
.psk-gbr__tiada { padding: 10px 12px; border-radius: 10px; background: var(--s-warn-soft, var(--s-fill)); border: 1px solid var(--s-warn-line, var(--s-line)); color: var(--s-warn-deep, var(--s-ink)); font-size: 13px; line-height: 1.55; }
/* gambar PRODUK yang dihantar kepada agent (laporan larian) */
.psk-gbrp { padding: 10px 12px; border-radius: 10px; background: var(--s-fill); border: 1px solid var(--s-line); color: var(--s-ink-2); font-size: 13px; line-height: 1.55; }
.psk-gbrp__senarai { margin: 6px 0 0; padding-left: 18px; }
.psk-prev { border: 1px solid var(--s-line); border-radius: 10px; overflow: hidden; }
.psk-prev__h { display: flex; align-items: center; gap: 10px; padding: 11px 13px; background: var(--s-fill); border-bottom: 1px solid var(--s-line); }
.psk-prev__t {
  width: 34px; height: 34px; flex: none; border-radius: 7px; display: grid; place-items: center;
  background: var(--s-card); border: 1px solid var(--s-line-strong); color: var(--s-ink-3);
}
.psk-prev__n { flex: 1; min-width: 0; font-size: 13px; color: var(--s-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.psk-prev__b { font-size: 11.5px; color: var(--s-ink-3); flex: none; }

.psk-sets { display: grid; grid-template-columns: repeat(var(--psk-kol, 3), minmax(0, 1fr)); }
.psk-set { padding: 13px; border-left: 1px solid var(--s-line); min-width: 0; }
.psk-set:first-child { border-left: 0; }
.psk-set__c {
  display: inline-block; margin-bottom: 11px; padding: 2px 8px; border-radius: 6px;
  background: var(--s-card); border: 1px solid var(--s-line);
  font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--s-ink-3);
}
.psk-f { margin-bottom: 10px; }
.psk-f:last-child { margin-bottom: 0; }
.psk-f__l {
  display: flex; align-items: center; gap: 5px; margin-bottom: 4px;
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--s-ink-3);
}
.psk-f__d { width: 6px; height: 6px; border-radius: 999px; flex: none; }
/* Tinggi TETAP, bukan ikut kandungan — kad sebenar memakai kotak teks
   bertinggi tetap (h-20 untuk medan pendek, h-56 untuk prompt), jadi baris
   yang sama berbaris rata merentas ketiga-tiga lajur. Tinggi ikut kandungan
   menjadikan lajur tersasar antara satu sama lain dan kad kelihatan senget.
   Nilai dikecilkan berkadar: pratonton ialah pengesahan, bukan editor. */
/* ⚠️ Tinggi dikira daripada BARIS PENUH, bukan nombor bulat yang sedap:
   12.5px × 1.55 = 19.4px sebaris, campur padding 18 dan bingkai 2.
   Nilai yang tidak menjadi gandaan baris memotong ayat di tengah huruf —
   kotak nampak rosak, bukan penuh. 2 baris untuk medan teks, 4 untuk prompt
   (prompt + baris Dialog memang memakan empat baris pada lebar lajur ini). */
.psk-f__v {
  height: 59px; overflow: auto; box-sizing: border-box; padding: 9px 10px; border-radius: 8px;
  background: var(--s-card); border: 1px solid var(--s-line-strong);
  font-size: 12.5px; line-height: 1.55; color: var(--s-ink); white-space: pre-wrap; overflow-wrap: anywhere;
}
.psk-f__v--panjang { height: 98px; }
.psk-f__v--kosong { color: var(--s-ink-3); font-style: italic; }

.psk-raw {
  margin: 0; max-height: 200px; overflow: auto; padding: 12px 13px; border-radius: 8px;
  background: var(--s-fill); color: var(--s-ink-2);
  font-family: var(--s-mono); font-size: 12px; line-height: 1.65; white-space: pre-wrap; word-break: break-word;
}

/* Telefon: apa-apa yang berkongsi baris di komputer diberi barisnya
   sendiri. Tanpa ini nama medan, blok dan tanda status berebut satu
   baris ~330px dan setiap satu keluar terpotong. */
@media (max-width: 640px) {
  .psk-row { flex-wrap: wrap; row-gap: 7px; }
  .psk-row__n { flex: 1 1 auto; }
  .psk-row__v { flex: 1 1 100%; order: 4; padding-left: 27px; white-space: normal; }
  .psk-status { flex-wrap: wrap; }
  /* Lebar = seluruh baris TOLAK tanda betul, supaya tanda itu kekal
     bersebelahan ayatnya dan hanya butang jatuh ke baris kedua. */
  .psk-status__c { flex: 1 1 calc(100% - 29px); }
  .psk-status__a { margin-left: 29px; }
  .psk-more__h { flex-wrap: wrap; row-gap: 3px; }
  .psk-more__d { flex: 1 1 100%; order: 3; }
  .psk-hero { padding: 22px 16px; }
  /* Kad sebenar juga menindan setnya pada telefon (grid-cols-1 md:grid-cols-3),
     jadi pratonton mengikutnya — tiga lajur pada 330px bermakna ~100px setiap
     satu dan tiada ayat yang boleh dibaca. */
  .psk-sets { grid-template-columns: minmax(0, 1fr); }
  .psk-set { border-left: 0; border-top: 1px solid var(--s-line); }
  .psk-set:first-child { border-top: 0; }
}
`;

/* Pecahkan templat kepada serpihan supaya blok @xxx boleh dirender
   sebagai cip. Satu fungsi dipakai oleh baris tertutup DAN pratonton. */
const pecah = (templat) => String(templat || '')
    .split(/(@[a-zA-Z0-9_]+)/g)
    .map((s, i) => (s.startsWith('@')
        ? { jenis: 'blok', teks: s, kunci: `b${i}` }
        /* Baris baharu jadi ruang; ruang di tepi DIKEKALKAN supaya tanda
           petikan dan titik bertindih kekal melekat pada blok. */
        : { jenis: 'teks', teks: s.replace(/\s+/g, ' '), kunci: `t${i}` }))
    .filter((p) => p.teks !== '');

/* ⭐ NAMA BLOK YANG PELANGGAN NAMPAK = teks yang dia (atau agent) betul-betul
   pilih daripada jawapan — BUKAN slugnya (arahan user 2026-08-20).
   Slug ialah kod dalaman: satu tajuk set penuh menjadi
   @set_1_s1_angle_4_framework_1_realization_background_cozy_living_room_...
   iaitu satu perkataan sepanjang satu baris yang langsung tidak boleh dibaca,
   dan pelanggan tidak dapat mengaitkannya dengan apa-apa yang dia serlah.
   ⛔ Kod itu KEKAL wujud — ia yang disisip ke dalam kotak teks (dan kotak teks
   memang memaparkannya, kerana di situ pelanggan menyunting kod sebenar).
   Yang bertukar hanyalah LABEL pada cip. */
const NAMA_MAKS = 32;
const namaBlok = (kod, peta) => {
    const penuh = peta && peta[kod];
    if (!penuh) return '@' + kod;              // blok tanpa label — papar kod
    return penuh.length > NAMA_MAKS ? penuh.slice(0, NAMA_MAKS - 1) + '…' : penuh;
};

/* Satu medan dikira "berpadan" bila templatnya menyebut sekurang-kurangnya
   satu blok yang benar-benar wujud dalam jawapan agent. Menaip perkataan
   biasa sahaja tidak memadai — enjin tiada apa-apa untuk diambil. */
const berpadan = (templat, sumber) => {
    const blok = [...String(templat || '').matchAll(/@([a-zA-Z0-9_]+)/g)].map((m) => m[1].toLowerCase());
    if (!blok.length) return false;
    const ada = new Set((sumber || []).map((s) => String(s).toLowerCase()));
    return blok.some((b) => ada.has(b));
};

/* ⚠️ ESLint webapp tiada `jsx-uses-vars`, jadi ikon yang datang daripada
   peta seperti ini WAJIB melalui pemboleh ubah `const` bermula huruf
   besar sebelum dirender (memory project_webapp_eslint_jsx_component_vars). */
/* Pecahkan teks kepada serpihan biasa + serpihan diserlah, ikut kedudukan
   penanda daripada enjin. Penanda dijamin tidak bertindih (enjin sudah
   membuang yang bersarang), tetapi ia disusun semula di sini supaya susunan
   pemanggil tidak boleh memecahkan paparan. */
const potongSerlah = (teks, penanda) => {
    const t = String(teks || '');
    const p = [...(penanda || [])].sort((a, b) => a.pos - b.pos);
    const keluar = [];
    let i = 0;
    p.forEach((m, n) => {
        if (m.pos < i) return;                       // bertindih — langkau
        if (m.pos > i) keluar.push({ kunci: 't' + n, teks: t.slice(i, m.pos) });
        keluar.push({ kunci: 'm' + n, teks: t.slice(m.pos, m.pos + m.len), serlah: true });
        i = m.pos + m.len;
    });
    if (i < t.length) keluar.push({ kunci: 'akhir', teks: t.slice(i) });
    return keluar;
};

const IKON = { gambar: IkonGambar, video: Video, teks: Type };

export default function ProStudioKonfigurV39({
    t,
    /* keadaan larian: 'idle' | 'jalan' | 'siap' */
    larian, barisLarian, onJalan, ralat = '',
    /* onUbahTetapan — ditekan daripada jalur hijau "Configure semula". Ia
       memulangkan pelanggan ke SKRIN PERSEDIAAN (keadaan 1), bukan terus
       menjalankan semula (arahan user 2026-08-20).

       Sebabnya: tetapan yang menentukan bentuk jawapan — produk ujian, cip
       Extended bagi ChatGPT, model bagi Gemini — semuanya hidup pada skrin
       persediaan itu. Dahulu butang ini menjalankan terus, jadi satu-satunya
       cara menukar mana-mana daripadanya ialah meneka apa yang tersimpan.
       Tanpa prop ini butang itu kekal menjalankan terus (rupa lakaran). */
    onUbahTetapan,
    /* onKembaliHasil — jalan keluar daripada skrin persediaan itu tanpa
       menjalankan apa-apa. Diberi HANYA apabila memang ada hasil untuk
       dikembalikan; kalau tidak butang itu membawa ke skrin kosong. */
    onKembaliHasil,
    /* saatLuar — pemasa milik pemanggil. Halaman hidup sudah mengiranya, dan
       pemasa itu HIDUP walaupun pelanggan melompat ke langkah lain di tengah
       larian; pemasa dalaman komponen ini bermula semula pada 0 setiap kali ia
       dipasang. Tanpa nilai luar, komponen jatuh ke pemasanya sendiri. */
    saatLuar,
    /* Kebenaran jalan datang dari pemanggil — halaman hidup turut menuntut
       pautan custom GPT, bukan produk sahaja. */
    bolehJalan = true, notaJalan = '',
    /* produk ujian. `produkSenarai` = [{ id, nama }] — halaman hidup memakai
       id doc Firestore, jadi nilai dan label BUKAN benda yang sama. */
    produkSenarai = [], produkUjian, onProduk, memuatProduk = false, produkNama = '',
    /* ⭐ pemilihProduk — pemilih SEBENAR yang dihantar masuk oleh halaman
       hidup (komponen `PemilihProduk` yang SAMA seperti Langkah 2), bukan
       salinan kedua. Sebabnya: dua pemilih yang ditulis berasingan pasti
       terpesong — Langkah 2 sudah memapar gambar + carian + panel berskrol
       240px dan senarai READY sahaja, manakala `<select>` browser di sini
       membuka setinggi skrin dengan nama-nama yang hampir serupa (aduan user
       2026-08-26).
       ⛔ Ia dihantar sebagai NOD, bukan disalin ke dalam fail ini — fail ini
          RUPA sahaja dan tidak boleh tahu apa itu produk READY, gambar, atau
          Firestore. Tanpa nod ini (mod lakaran) ia jatuh ke `<Sel>` di bawah,
          jadi lakaran tidak pecah. */
    pemilihProduk = null,
    /* apa yang agent hasilkan — dipakai pada ayat ringkasan */
    set = 1, babak = 1, tarikh = '',
    /* (2026-09-27) larianBaharu = jawapan di skrin datang dari Configure terkini (bukan
       simpanan lama); belumSimpan = perlu tekan Save; chatUrl = perbualan agent asal. */
    larianBaharu = false, belumSimpan = false, chatUrl = '',
    /* medan + padanan */
    kumpulan = [], sumber = [], sumberLabel = {}, padanan = {}, onPadanan,
    /* tetapan lanjutan */
    platform = 'chatgpt', modelGemini = '', onModelGemini, modelSenarai = [], modelNota = '',
    onBacaModel, bacaModelSibuk = false, modelRalat = '',
    /* ChatGPT: cip "Extended". null = agent Gemini (kawalan ini tidak berkenaan).
       '' = belum dipilih · 'kekal' · 'mati'. */
    extended = null, onExtended,
    /* ChatGPT: tahap Thinking dibaca dari akaun (2026-09-26). null = agent Gemini.
       Menggantikan paparan cip Extended; `extended` lama hanya untuk nota agent lama. */
    tahap = null, tahapPilihan = [], akaunChatgpt = '', onTahap, onBacaTahap,
    /* (2026-09-27) akaun ChatGPT tiada kawalan Thinking → papar nota, Configure dibenarkan. */
    tahapTiada = false,
    /* Gemini: "Extended thinking" prominent (padanan cip Extended ChatGPT).
       null = agent ChatGPT · '' = belum dipilih · 'on' · 'off'. */
    geminiExtended = null, onGeminiExtended,
    /* pusingan jawapan — hanya dipapar bila agent memang menjawab lebih sekali */
    pusingan = [], pusinganPilih = 0, onPusingan, pusinganSibuk = false,
    jawapanPenuh = '',
    /* blok buatan pelanggan */
    blokSendiri = [], onTambahBlok, onBuangBlok, penandaBlok = [],
    /* pratonton */
    pratonton = [],
    /* ★ Gambar daripada jawapan agent (2026-09-11) — PRATONTON sahaja. `gambar` =
       [{ dataUrl }] yang extension pulangkan pada larian terakhir; `gambarDijangka`
       = bilangan pusingan yang pelanggan tanda "Simpan gambar" di Langkah 2. Blok
       ini dipapar HANYA bila ada yang dijangka — dan bila tiada satu pun diterima,
       ia menyatakannya (extension lama / agent tidak menjana gambar). */
    gambar = [], gambarDijangka = 0,
    /* ★ Laporan gambar PRODUK yang dihantar kepada agent (2026-09-13) — hasil
       `ringkasGambarProduk`: { status: 'lengkap'|'kurang'|'lama', dihantar, dijangka,
       pendua, amaran[] }. null = produk ujian tiada gambar tambahan → tiada paparan. */
    gambarProduk = null,
    /* ★ Tetapan larian yang TIDAK menjadi pada larian Configure terakhir (2026-09-13) —
       [{ kunci, vars }]: ChatGPT "Matikan Thinking" gagal · Gemini Extended thinking /
       model tidak dapat ditetapkan. Tanpa amaran ini pelanggan menyangka kalibrasi
       dibuat dengan tetapan pilihannya — pepijat lama itu SENYAP. */
    amaranTetapan = [],
}) {
    const [buka, setBuka] = useState(null);        // id medan yang sedang diedit
    /* (Sejarah — kini TERBUKA, lihat lihatLanjutan di bawah.) Laci lanjutan dahulu bermula tertutup. Ia dulu terbuka sendiri untuk
       agent Gemini tanpa senarai model — kecualian itu gugur 2026-08-21 apabila
       pemilih model keluar daripada laci ini; ia kini duduk pada borang utama
       (skrin persediaan) dan di bawah tajuk seksyen padanan (skrin hasil). */
    /* ⭐ TERBUKA secara lalai (arahan user 2026-08-21). Ini pertukaran yang
       DISENGAJAKAN: laci terbuka menolak senarai medan ~200px ke bawah pada
       setiap agent, tetapi kotak jawapan itu satu-satunya tempat pelanggan
       boleh membetulkan blok yang salah bentuk — dan kawalan yang tersembunyi
       dalam laci tertutup sama seperti tidak wujud. Pelanggan boleh menutupnya
       sendiri. ⛔ Jangan tukar balik kepada `false` tanpa bertanya. */
    const [lihatBlok, setLihatBlok] = useState(true);
    const [blokPesan, setBlokPesan] = useState('');
    /* Teks yang diserlah, dirakam pada mousedown — lihat nota pada butang. */
    const pilihanRef = useRef('');
    const rawRef = useRef(null);
    /* ⭐ Laci "Tetapan lanjutan" TERBUKA secara lalai (arahan user 2026-09-27) —
       dahulu tertutup (nota di atas). Pelanggan boleh menutupnya sendiri.
       ⛔ Jangan tukar balik kepada `false` tanpa bertanya. */
    const [lihatLanjutan, setLihatLanjutan] = useState(true);
    const [saat, setSaat] = useState(0);
    const taRef = useRef(null);
    const selRef = useRef(null);

    /* Pemasa saat semasa larian — bukti nyata ia sedang bekerja.
       Pelanggan menunggu 1–2 minit; skrin beku tanpa nombor dibaca
       sebagai "tergantung".

       ⚠️ Pemasa DIPULANGKAN ke sifar semasa render, bukan dalam effect.
       Lint projek (`react-hooks/set-state-in-effect`) menolak setState
       terus dalam badan effect, dan react.dev memang mengesyorkan corak
       "laraskan keadaan bila prop berubah" ini. Panggilan setState dalam
       callback setInterval di bawah tidak terjejas. */
    const [larianLepas, setLarianLepas] = useState(larian);
    if (larianLepas !== larian) { setLarianLepas(larian); setSaat(0); }

    useEffect(() => {
        if (larian !== 'jalan') return undefined;
        const id = setInterval(() => setSaat((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, [larian]);

    const saatPapar = Number.isFinite(saatLuar) ? saatLuar : saat;

    /* Pemasa saat semasa PADAN SEMULA (tukar pusingan jawapan) — corak sama
       seperti pemasa larian di atas: dipulangkan ke sifar semasa render, bukan
       dalam effect, supaya setiap padanan baharu bermula dari 0s. */
    const [saatPadan, setSaatPadan] = useState(0);
    const [sibukLepas, setSibukLepas] = useState(pusinganSibuk);
    if (sibukLepas !== pusinganSibuk) { setSibukLepas(pusinganSibuk); setSaatPadan(0); }

    useEffect(() => {
        if (!pusinganSibuk) return undefined;
        const id = setInterval(() => setSaatPadan((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, [pusinganSibuk]);
    const kelasSibuk = pusinganSibuk ? 'psk-sibuk' : undefined;

    const semuaMedan = kumpulan.flatMap((g) => g.medan);
    const kurang = semuaMedan.filter((f) => !berpadan(padanan[f.id], sumber));
    /* Baris yang blok-nya berpadan TETAPI bilangan kemunculannya tidak menepati
       bilangan set/babak. Berasingan daripada `kurang` — baris ini ADA sumber. */
    const amaranBlok = semuaMedan.filter((f) => f.nota);

    /* Baris yang gagal berpadan dibuka SENDIRI — ia satu-satunya baris
       yang benar-benar menuntut tindakan. Dilaraskan semasa render (bukan
       dalam effect) mengikut corak rasmi React "laraskan keadaan bila prop
       berubah"; `dilihat` menghalangnya daripada membuka semula baris yang
       pelanggan baru sahaja tutup. */
    const [dilihat, setDilihat] = useState('');
    const capKurang = `${larian}:${kurang.map((f) => f.id).join('|')}`;
    if (larian === 'siap' && dilihat !== capKurang) {
        setDilihat(capKurang);
        setBuka(kurang.length ? kurang[0].id : null);
    }

    const simpanKursor = () => {
        const ta = taRef.current;
        if (ta) selRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
    };

    /* Klik cip melepaskan fokus kotak teks, jadi kedudukan kursor dibaca
       daripada nilai yang DISIMPAN, bukan daripada kotak selepas blur. */
    const selitBlok = (medanId, slug) => {
        const nilai = String(padanan[medanId] || '');
        const s = selRef.current || {};
        const mula = Math.min(Number.isFinite(s.start) ? s.start : nilai.length, nilai.length);
        const habis = Math.min(Number.isFinite(s.end) ? s.end : nilai.length, nilai.length);
        const perluRuang = mula > 0 && !/\s$/.test(nilai.slice(0, mula));
        const sisip = `${perluRuang ? ' ' : ''}@${slug}`;
        const baru = nilai.slice(0, mula) + sisip + nilai.slice(habis);
        const p = mula + sisip.length;
        selRef.current = { start: p, end: p };
        onPadanan(medanId, baru);
        requestAnimationFrame(() => {
            const ta = taRef.current;
            if (ta) { ta.focus(); ta.setSelectionRange(p, p); }
        });
    };

    /* ══════════ KEADAAN 1 — BELUM PERNAH DIJALANKAN ══════════ */
    if (larian === 'idle') {
        return (
            <div className="psk">
                <style>{GAYA}</style>
                {/* ⛔ TIADA tajuk di sini. Kepala Kad di atas sudah bertanya
                    soalannya ("Mari kita uji sekali") — tajuk kedua yang berbunyi
                    sama hanya menolak borang ke bawah tanpa memberitahu apa-apa
                    yang baharu. */}
                {ralat ? <div className="psk-ralat">{ralat}</div> : null}
                <div className="psk-hero">
                    <div className="psk-hero__ikon"><Play size={18} strokeWidth={1.9} /></div>
                    <p className="psk-hero__d">{t(platform === 'gemini' ? 'prostudio.cfg.introBodyGemini' : 'prostudio.cfg.introBody')}</p>

                    <div className="psk-hero__form">
                        <Lbl>{t('prostudio.wizard.testProduct')}</Lbl>
                        {/* Pemilih yang SAMA seperti Langkah 2 apabila halaman hidup
                            menghantarnya. `<Sel>` di bawah kekal untuk LAKARAN sahaja
                            (lakaran tiada produk sebenar, tiada gambar). */}
                        {pemilihProduk || (<>
                            <Sel
                                auto={!produkUjian} value={produkUjian}
                                disabled={memuatProduk || produkSenarai.length === 0}
                                onChange={(e) => onProduk(e.target.value)}
                            >
                                <option value="">
                                    {memuatProduk ? t('prostudio.agentsec.productsLoading')
                                        : produkSenarai.length === 0 ? t('prostudio.agentsec.productsEmpty')
                                            : t('prostudio.wizard.pickProduct')}
                                </option>
                                {produkSenarai.map((p) => <option key={p.id} value={p.id}>{p.nama}</option>)}
                            </Sel>
                            {!memuatProduk && produkSenarai.length === 0 && (
                                <p className="psk-h__d">{t('prostudio.agentsec.productsEmptyHint')}</p>
                            )}
                        </>)}

                        {/* ⭐ Cip "Extended" DI SINI, bukan dalam laci lanjutan
                            (arahan user 2026-08-20: "letak dlm section configure tu
                            dan wajib pilih"). Ia menentukan bentuk jawapan yang
                            dikalibrasi, jadi ia keputusan larian — bukan tetapan
                            pakar. Tiada pilihan lalai: butang Configure kekal mati
                            sehingga pelanggan memilih, supaya tiada pilihan dibuat
                            secara senyap bagi pihaknya. */}
                        {/* ★ Tahap Thinking ChatGPT (2026-09-26, "Lakaran A" — tanpa huraian,
                            ikut ChatGPT sendiri). Pilihan DIBACA dari akaun pelanggan (Plus:
                            Instant/Medium/High · Go: Instant/Think) — corak senarai model Gemini.
                            Wajib pilih sebelum Configure. */}
                        {tahap !== null && onTahap && (
                            <div style={{ marginTop: 16 }}>
                                <Lbl>{t('prostudio.cfg.tahapLabel')}</Lbl>
                                {tahapPilihan.length === 0 ? (
                                    <div className="psk-model">
                                        {onBacaTahap && (
                                            <Btn variant="ghost" size="sm" disabled={bacaModelSibuk}
                                                icon={bacaModelSibuk
                                                    ? <Loader2 size={13} strokeWidth={2} className="psk-spin" />
                                                    : <ListRestart size={13} strokeWidth={2} />}
                                                onClick={onBacaTahap}>
                                                {bacaModelSibuk ? t('prostudio.cfg.tahapReading') : t('prostudio.cfg.tahapRead')}
                                            </Btn>
                                        )}
                                        <span className="psk-model__h">{t('prostudio.cfg.tahapReadHint')}</span>
                                    </div>
                                ) : (
                                    <div className="psk-pilih">
                                        {tahapPilihan.map((p) => (
                                            <button
                                                key={p} type="button"
                                                className={`psk-pilih__b${tahap === p ? ' psk-pilih__b--on' : ''}`}
                                                onClick={() => onTahap(p)}
                                            >
                                                <span className="psk-pilih__r">
                                                    {tahap === p && <Check size={11} strokeWidth={3} />}
                                                </span>
                                                <span className="psk-pilih__c">
                                                    <span className="psk-pilih__t">{p}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {modelRalat ? <p className="psk-h__d psk-h__d--stop">{modelRalat}</p> : null}
                                {tahapPilihan.length === 0 && tahapTiada && !modelRalat && (
                                    <p className="psk-h__d">
                                        {akaunChatgpt ? `${t('prostudio.cfg.tahapAkaun', { akaun: akaunChatgpt })} · ` : ''}
                                        {t('prostudio.cfg.tahapTiada')}
                                    </p>
                                )}
                                {tahapPilihan.length > 0 && (
                                    <p className={`psk-h__d${tahap ? '' : ' psk-h__d--amaran'}`}>
                                        {akaunChatgpt ? `${t('prostudio.cfg.tahapAkaun', { akaun: akaunChatgpt })} · ` : ''}
                                        {tahap ? t('prostudio.cfg.tahapHint') : t('prostudio.cfg.tahapBlocked')}
                                        {onBacaTahap && !bacaModelSibuk && (
                                            <> · <button type="button" className="psk-pautan" onClick={onBacaTahap}>{t('prostudio.cfg.tahapReadAgain')}</button></>
                                        )}
                                    </p>
                                )}
                                {!tahap && !tahapTiada && extended === 'mati' && (
                                    <p className="psk-h__d">{t('prostudio.cfg.tahapLama')}</p>
                                )}
                            </div>
                        )}

                        {tahap === null && extended !== null && onExtended && (
                            <div style={{ marginTop: 16 }}>
                                <Lbl>{t('prostudio.cfg.extLabel')}</Lbl>
                                <div className="psk-pilih">
                                    {[
                                        { nilai: 'kekal', tajuk: t('prostudio.cfg.extKeep'), sub: t('prostudio.cfg.extKeepDesc') },
                                        { nilai: 'mati', tajuk: t('prostudio.cfg.extOff'), sub: t('prostudio.cfg.extOffDesc') },
                                    ].map((o) => (
                                        <button
                                            key={o.nilai} type="button"
                                            className={`psk-pilih__b${extended === o.nilai ? ' psk-pilih__b--on' : ''}`}
                                            onClick={() => onExtended(o.nilai)}
                                        >
                                            <span className="psk-pilih__r">
                                                {extended === o.nilai && <Check size={11} strokeWidth={3} />}
                                            </span>
                                            <span className="psk-pilih__c">
                                                <span className="psk-pilih__t">{o.tajuk}</span>
                                                <span className="psk-pilih__d">{o.sub}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <p className={`psk-h__d${extended ? '' : ' psk-h__d--amaran'}`}>
                                    {extended ? t('prostudio.cfg.extHint') : t('prostudio.cfg.extBlocked')}
                                </p>
                            </div>
                        )}

                        {/* ⭐ GEMINI: model + tahap Thinking — prominent + wajib,
                            padanan tahap Thinking ChatGPT (arahan user 2026-08-20).
                            Gemini ada DUA kawalan (model + tahap fikir), jadi
                            dua-dua kotak pilih DI SINI; butang Configure kekal mati
                            sehingga kedua-duanya dipilih. */}
                        {platform === 'gemini' && (
                            <div style={{ marginTop: 16 }}>
                                <Lbl>{t('prostudio.cfg.geminiModelLabel')}</Lbl>
                                {modelSenarai.length === 0 ? (
                                    /* Senarai belum dibaca — butang baca DI SINI, bukan
                                       tersorok dalam laci. Tanpa senarai, tiada kotak
                                       model untuk dipilih. */
                                    <div className="psk-model">
                                        {onBacaModel && (
                                            <Btn variant="ghost" size="sm" disabled={bacaModelSibuk}
                                                icon={bacaModelSibuk
                                                    ? <Loader2 size={13} strokeWidth={2} className="psk-spin" />
                                                    : <ListRestart size={13} strokeWidth={2} />}
                                                onClick={onBacaModel}>
                                                {bacaModelSibuk ? t('prostudio.cfg.modelReading') : t('prostudio.cfg.modelRead')}
                                            </Btn>
                                        )}
                                        <span className="psk-model__h">{t('prostudio.cfg.modelReadHint')}</span>
                                    </div>
                                ) : (
                                    <div className="psk-pilih">
                                        {modelSenarai.map((m) => (
                                            <button
                                                key={m} type="button"
                                                className={`psk-pilih__b${modelGemini === m ? ' psk-pilih__b--on' : ''}`}
                                                onClick={() => onModelGemini(m)}
                                            >
                                                <span className="psk-pilih__r">
                                                    {modelGemini === m && <Check size={11} strokeWidth={3} />}
                                                </span>
                                                <span className="psk-pilih__c">
                                                    <span className="psk-pilih__t">{m}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {modelRalat ? <p className="psk-h__d psk-h__d--stop">{modelRalat}</p> : null}
                                {/* ★ 2026-09-27 (arahan user): senarai sudah ada → BUTANG baca semula,
                                    rupa SAMA butang "Baca senarai model saya" (pautan teks kecil
                                    ditolak user — "kecik sgt, tak perasan"). Google menukar nama
                                    model (3.7 → 3.8 Flash); tanpa ini senarai lama kekal. */}
                                {modelSenarai.length > 0 && onBacaModel && (
                                    <div className="psk-model" style={{ marginTop: 10 }}>
                                        <Btn variant="ghost" size="sm" disabled={bacaModelSibuk}
                                            icon={bacaModelSibuk
                                                ? <Loader2 size={13} strokeWidth={2} className="psk-spin" />
                                                : <ListRestart size={13} strokeWidth={2} />}
                                            onClick={onBacaModel}>
                                            {bacaModelSibuk ? t('prostudio.cfg.modelReading') : t('prostudio.cfg.modelReadAgain')}
                                        </Btn>
                                        <span className="psk-model__h">{t('prostudio.cfg.modelDibaca')}</span>
                                    </div>
                                )}

                                {geminiExtended !== null && onGeminiExtended && (
                                    <div style={{ marginTop: 16 }}>
                                        {/* ★ 2026-10-11: Gemini 3 menggantikan suis "Extended thinking"
                                            dengan tiga tahap. Tajuk = perkataan Gemini sendiri (tidak
                                            diterjemah). Nilai + pemetaan agent lama: utils/geminiPemilih.js. */}
                                        <Lbl>{t('prostudio.cfg.geminiTahapLabel')}</Lbl>
                                        <div className="psk-pilih">
                                            {[
                                                { nilai: 'low', tajuk: 'Low', sub: t('prostudio.cfg.geminiTahapLowDesc') },
                                                { nilai: 'medium', tajuk: 'Medium', sub: t('prostudio.cfg.geminiTahapMediumDesc') },
                                                { nilai: 'high', tajuk: 'High', sub: t('prostudio.cfg.geminiTahapHighDesc') },
                                            ].map((o) => (
                                                <button
                                                    key={o.nilai} type="button"
                                                    className={`psk-pilih__b${geminiExtended === o.nilai ? ' psk-pilih__b--on' : ''}`}
                                                    onClick={() => onGeminiExtended(o.nilai)}
                                                >
                                                    <span className="psk-pilih__r">
                                                        {geminiExtended === o.nilai && <Check size={11} strokeWidth={3} />}
                                                    </span>
                                                    <span className="psk-pilih__c">
                                                        <span className="psk-pilih__t">{o.tajuk}</span>
                                                        <span className="psk-pilih__d">{o.sub}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                        <p className={`psk-h__d${(modelGemini && geminiExtended) ? '' : ' psk-h__d--amaran'}`}>
                                            {(modelGemini && geminiExtended) ? t('prostudio.cfg.geminiHint') : t('prostudio.cfg.geminiBlocked')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="psk-hero__btn">
                        {/* Jalan keluar bila pelanggan masuk ke sini daripada jalur
                            hijau dan berubah fikiran. Tanpa ia, satu-satunya cara
                            keluar ialah menjalankan semula larian yang sebenarnya
                            tidak dikehendaki (dan larian itu memakan masa + menyentuh
                            akaun ChatGPT/Gemini pelanggan). */}
                        {onKembaliHasil && (
                            <Btn variant="ghost" onClick={onKembaliHasil}>
                                {t('prostudio.cfg.backResult')}
                            </Btn>
                        )}
                        <Btn
                            icon={<Play size={14} strokeWidth={2} />}
                            disabled={!bolehJalan || !produkUjian}
                            title={notaJalan || (produkUjian ? t('prostudio.agentsec.runReady') : t('prostudio.agentsec.runBlockedProduct'))}
                            onClick={onJalan}
                        >
                            {t('prostudio.cfg.run')}
                        </Btn>
                    </div>
                </div>

                {/* ⛔ TIADA laci "Tetapan lanjutan" pada skrin persediaan. Model +
                    Extended thinking sudah prominent di borang utama, dan pusingan
                    jawapan hanya wujud SELEPAS larian siap — jadi laci di sini
                    tidak akan pernah memaparkan apa-apa. */}
            </div>
        );
    }

    /* ══════════ KEADAAN 2 — SEDANG BERJALAN ══════════ */
    if (larian === 'jalan') {
        const langkahLarian = [
            t('prostudio.wizard.run1'),
            t('prostudio.wizard.run2'),
            t('prostudio.wizard.run3'),
        ];
        return (
            <div className="psk">
                <style>{GAYA}</style>
                <div className="psk-hero">
                    <div className="psk-hero__ikon"><Loader2 size={18} strokeWidth={1.9} className="psk-spin" /></div>
                    <h3 className="psk-hero__t">{t('prostudio.cfg.runningTitle')}</h3>
                    <p className="psk-hero__d">{t('prostudio.cfg.runningBody')}</p>

                    <div className="psk-hero__form">
                        <div className="psk-run">
                            {langkahLarian.map((b, i) => {
                                const siap = barisLarian > i;
                                const kini = barisLarian === i;
                                return (
                                    <div key={b} className={`psk-run__b${siap ? ' psk-run__b--siap' : kini ? ' psk-run__b--kini' : ''}`}>
                                        <span className="psk-run__d">
                                            {siap ? <Check size={11} strokeWidth={2.6} />
                                                : kini ? <Loader2 size={11} strokeWidth={2.4} className="psk-spin" /> : null}
                                        </span>
                                        <span>{b}</span>
                                        {kini && <span className="psk-run__t">{saatPapar}s</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ══════════ KEADAAN 3 — SUDAH SIAP ══════════ */
    const jum = semuaMedan.length;
    const ok = jum - kurang.length;

    return (
        <div className="psk">
            <style>{GAYA}</style>

            {ralat ? <div className="psk-ralat">{ralat}</div> : null}

            {/* Jalur status — bertukar kepada keadaan SIBUK semasa padan semula
                (tukar pusingan jawapan dalam Tetapan lanjutan). Ia di atas skrin,
                jadi pelanggan yang sudah menatal ke atas tetap nampak sistem sedang
                bekerja; laci di bawah memapar tanda yang sama di tempat dia menekan. */}
            <div className={`psk-status${pusinganSibuk ? ' psk-status--sibuk' : ''}`}
                role={pusinganSibuk ? 'status' : undefined} aria-live="polite">
                <span className="psk-status__i">
                    {pusinganSibuk
                        ? <Loader2 size={17} strokeWidth={2.2} className="psk-spin" />
                        : <Check size={17} strokeWidth={2.4} />}
                </span>
                <div className="psk-status__c">
                    <div className="psk-status__t">
                        {pusinganSibuk ? t('prostudio.cfg.rematchTitle')
                            : (larianBaharu && belumSimpan) ? t('prostudio.cfg.freshTitle')
                                : t('prostudio.cfg.readyTitle')}
                    </div>
                    {/* Tiada tarikh = agent yang BARU sahaja dijalankan buat kali
                        pertama (ia belum disimpan, jadi belum ada tarikh). Ayat
                        berasingan, bukan tarikh "—" di tengah ayat.
                        (2026-09-27) Larian baharu → masa larian itu + peringatan Save,
                        supaya pelanggan tahu jawapan di skrin ialah yang TERKINI. */}
                    <div className="psk-status__d">
                        {pusinganSibuk
                            ? t('prostudio.cfg.rematchBody')
                            : <>
                                {!tarikh
                                    ? t('prostudio.cfg.readyMetaBaharu', { produk: produkNama || '—' })
                                    : (larianBaharu && belumSimpan)
                                        ? t('prostudio.cfg.freshMeta', { tarikh, produk: produkNama || '—' })
                                        : t('prostudio.cfg.readyMeta', { tarikh, produk: produkNama || '—' })}
                                {larianBaharu && belumSimpan && <> · <b>{t('prostudio.cfg.freshSave')}</b></>}
                                {chatUrl && <> · <a className="psk-pautan" href={chatUrl} target="_blank" rel="noopener noreferrer">{t('prostudio.cfg.bukaPerbualan')}</a></>}
                            </>}
                    </div>
                </div>
                {/* Semasa padan semula, butang "Configure semula" diganti pemasa saat —
                    memulakan larian baharu di tengah padanan akan bertembung dengannya. */}
                {pusinganSibuk ? (
                    <span className="psk-status__s">{saatPadan}s</span>
                ) : (
                    <Btn className="psk-status__a" variant="ghost"
                        icon={<RotateCcw size={14} strokeWidth={2} />}
                        onClick={onUbahTetapan || onJalan}>
                        {t('prostudio.cfg.again')}
                    </Btn>
                )}
            </div>

            {amaranTetapan.map((a) => (
                <div key={a.kunci} className="psk-gbr__tiada">{t(a.kunci, a.vars)}</div>
            ))}

            {/* ★ Gambar PRODUK yang dihantar kepada agent (2026-09-13) — hanya bila produk
                ujian membawa gambar tambahan. Kekurangan dipapar amber bersama sebabnya. */}
            {gambarProduk && (
                <div className={gambarProduk.status === 'lengkap' ? 'psk-gbrp' : 'psk-gbr__tiada'}>
                    {gambarProduk.status === 'lama'
                        ? t('prostudio.cfg.prodImgOld')
                        : gambarProduk.status === 'kurang'
                            ? t('prostudio.cfg.prodImgShort', { dihantar: gambarProduk.dihantar, dijangka: gambarProduk.dijangka })
                            : t('prostudio.cfg.prodImgOk', { n: gambarProduk.dihantar })}
                    {gambarProduk.amaran.length > 0 && (
                        <ul className="psk-gbrp__senarai">
                            {gambarProduk.amaran.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {/* ★ Gambar daripada jawapan agent — pratonton sahaja (2026-09-11). Diletak
                di ATAS kad pratonton: ia hasil larian yang paling mudah dinilai sekali
                pandang ("betul ke gambar yang diambil?"). */}
            {gambarDijangka > 0 && (
                <div className="psk-gbr">
                    <div className="psk-tajuk">{t('prostudio.cfg.imgTitle')}</div>
                    <div className="psk-h__d" style={{ marginBottom: 10 }}>{t('prostudio.cfg.imgHint')}</div>
                    {gambar.length > 0 ? (
                        <div className="psk-gbr__baris">
                            {gambar.map((g, i) => (
                                <img key={i} className="psk-gbr__img" src={g.dataUrl} alt="" />
                            ))}
                        </div>
                    ) : (
                        <div className="psk-gbr__tiada">{t('prostudio.cfg.imgNone')}</div>
                    )}
                </div>
            )}

            {/* ⭐ PRATONTON DI ATAS, SENTIASA TERBUKA (arahan user 2026-08-19).
                Ia dahulunya laci tertutup di bawah senarai padanan. Tetapi kad
                produk itulah benda yang pelanggan kenal — dia tahu rupanya, dan
                dia boleh menilai "betul ke tak" dengan sekali pandang. Senarai
                padanan di bawah ialah kawalan untuk MEMBAIKI apa yang dia nampak
                salah di sini, jadi hasil didahulukan, kawalan mengikut. */}
            {pratonton.length > 0 && (
                <div className={kelasSibuk} aria-busy={pusinganSibuk || undefined}>
                    <div className="psk-tajuk">{t('prostudio.cfg.preview')}</div>
                    <div className="psk-h__d" style={{ marginBottom: 10 }}>{t('prostudio.cfg.previewHint')}</div>
                    <div className="psk-prev">
                    <div className="psk-prev__h">
                        <span className="psk-prev__t"><IkonGambar size={16} strokeWidth={1.7} /></span>
                        <span className="psk-prev__n">{produkNama || '—'}</span>
                    </div>
                    <div className="psk-sets" style={{ '--psk-kol': pratonton.length }}>
                        {pratonton.map((s) => (
                            <div key={s.id} className="psk-set">
                                <span className="psk-set__c">{s.tajuk}</span>
                                {s.baris.map((r) => (
                                    <div key={r.id} className="psk-f">
                                        <div className="psk-f__l">
                                            <span className="psk-f__d" style={{ background: r.warna }} />
                                            {r.label}
                                        </div>
                                        <div className={`psk-f__v${r.panjang ? ' psk-f__v--panjang' : ''}${r.nilai ? '' : ' psk-f__v--kosong'}`}>
                                            {r.nilai || t('prostudio.cfg.previewEmpty')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            )}

            <div className={kelasSibuk} aria-busy={pusinganSibuk || undefined}>
                <div className="psk-tajuk">{t('prostudio.cfg.resultTitle')}</div>
                {/* ⛔ "Kesemua medan berpadan" TIDAK boleh dipapar ketika ada
                    amaran blok. Medan dikira "berpadan" sebaik ia menuding kepada
                    satu blok — bukan bermakna blok itu benar-benar ada dalam
                    jawapan, atau muncul cukup kali. Memapar kedua-dua ayat
                    serentak membuatkan skrin bercakap dua benda bertentangan
                    (dilihat pada halaman hidup, 2026-08-26). */}
                <div className={`psk-h__d${(kurang.length || amaranBlok.length) ? ' psk-h__d--amaran' : ''}`}>
                    {kurang.length
                        ? t('prostudio.cfg.resultSome', { n: ok, jum, kurang: kurang.length })
                        : amaranBlok.length
                            ? t('prostudio.cfg.resultBlokAmaran', { n: amaranBlok.length })
                            : t('prostudio.cfg.resultAll', { set, babak, jum })}
                </div>
                {/* Kedua-duanya berlaku serentak → amaran blok jadi baris kedua,
                    supaya ia tidak hilang di sebalik kiraan medan. */}
                {kurang.length > 0 && amaranBlok.length > 0 && (
                    <div className="psk-h__d psk-h__d--amaran">
                        {t('prostudio.cfg.resultBlokAmaran', { n: amaranBlok.length })}
                    </div>
                )}

                {/* ⛔ Paparan "Full answer from the agent" DIBUANG (arahan user
                    2026-08-21). Jawapan yang sama dipapar di dalam "Blok anda
                    sendiri" di bawah — dan yang itu BOLEH diserlah untuk mencipta
                    blok. Dua salinan jawapan yang sama pada satu skrin membuat
                    pelanggan tertanya-tanya yang mana satu ia patut sentuh.
                    ⛔ Jangan pulangkan salinan kedua ini. */}
            </div>

            {/* ⭐ Model Gemini — KOTAK TETAP, rupa sama seperti kotak lain pada skrin ini
                (arahan user 2026-09-13: "utk model gemini tu pun cacat tak ikut format
                section yg lain"). Bingkai + kepala berwarna seperti laci, TETAPI tiada
                anak panah dan tidak boleh ditutup — pilihan user: pemilih model penting
                dan membawa amaran bila belum dipilih; kotak yang boleh ditutup mudah
                terlepas. Dahulu ia label kecil + kotak pilihan terapung di bawah tajuk
                "Apa yang SESAAT ambil daripada jawapan".
                Asal (2026-08-21): model Gemini DI SINI, bukan dalam laci lanjutan.
                ⛔ SENGAJA TIADA butang "Baca senarai model" di skrin ini: butang itu
                sudah ada pada skrin persediaan (Configure dan Configure semula), dan dua
                salinan bermakna pelanggan tertanya-tanya sama ada ia dua benda berlainan.
                Dimalapkan + dikunci semasa padan semula, sama seperti seksyen di atasnya
                (dahulu ia duduk DI DALAM pembalut `kelasSibuk` seksyen itu). */}
            {platform === 'gemini' && (
                <div className={`psk-more psk-more--tetap${pusinganSibuk ? ' psk-sibuk' : ''}`}
                    aria-busy={pusinganSibuk || undefined}>
                    <div className="psk-more__h psk-more__h--tetap">
                        <span className="psk-more__t">{t('prostudio.agentsec.geminiModel')}</span>
                        <span className="psk-more__d">{t('prostudio.cfg.modelBoxHint')}</span>
                    </div>
                    <div className="psk-more__b">
                        <div>
                            <Sel auto={!modelGemini} value={modelGemini}
                                onChange={(e) => onModelGemini?.(e.target.value)}>
                                <option value="">{t('prostudio.agentsec.geminiModelDefault')}</option>
                                {modelSenarai.map((m) => <option key={m} value={m}>{m}</option>)}
                            </Sel>
                            {/* ⚠️ AMARAN, bukan nota. "Ikut tetapan Gemini" bukan bermaksud
                                "biarkan SESAAT pilih yang terbaik" — ia bermaksud SESAAT
                                langsung TIDAK menyentuh menu model, jadi Gem itu berjalan
                                atas apa sahaja yang browser itu sedang tunjuk. */}
                            {!modelGemini && <p className="psk-amaran">{t('prostudio.cfg.modelWarn')}</p>}
                            {/* Nota hanya bila senarai model memang ada. Ayat versi
                                "senarai kosong" menyuruh pelanggan menekan butang di
                                bawah — dan butang itu sengaja tiada di skrin ini. */}
                            {modelSenarai.length > 0 && modelNota
                                ? <p className="psk-h__d">{modelNota}</p> : null}
                        </div>
                    </div>
                </div>
            )}

            {/* ⭐ Tetapan lanjutan DI SINI — terus di bawah kotak Model Gemini (arahan
                user 2026-09-13: "letak di bawah section model gemini utk gemini. buat
                juga utk chatgpt"). Agent ChatGPT tiada seksyen model, jadi laci ini
                duduk pada kedudukan yang SAMA: terus di bawah tajuk seksyen padanan.
                Dahulu ia di hujung skrin, di bawah senarai medan — sedangkan pilihan
                di dalamnya ("Output diambil daripada") menentukan jawapan yang dibaca
                oleh SEMUA kawalan di bawahnya (Blok anda sendiri + senarai medan).
                ⚠️ SENGAJA di LUAR pembalut `kelasSibuk` seksyen di atas: semasa padan
                semula pembalut itu dimalapkan + dikunci, sedangkan laci ini memapar
                tanda sibuk + pemasa saat di tempat pelanggan menekan — ia mesti kekal
                terang. Jarak antara blok datang daripada `.psk` (grid gap 18px). */}
            <Lanjutan
                t={t} buka={lihatLanjutan} setBuka={setLihatLanjutan}
                pusingan={pusingan} pusinganPilih={pusinganPilih}
                onPusingan={onPusingan} pusinganSibuk={pusinganSibuk} saatPadan={saatPadan} siap
            />

            {/* ── Blok anda sendiri ──────────────────────────────────────
                Blok yang dikesan AI kadang membawa JAWAPAN dalam namanya
                (contoh sebenar: `@hook_konon_nak_rasa_sikit`), jadi ia tidak
                pernah berpadan lagi pada produk seterusnya. Di sini pelanggan
                boleh menandakan LABELnya sendiri — contohnya `HOOK:` — yang
                berpadan pada setiap set dan setiap produk. */}
            {onTambahBlok && (
                <div className={`psk-more${lihatBlok ? ' psk-more--buka' : ''}${pusinganSibuk ? ' psk-sibuk' : ''}`}
                    aria-busy={pusinganSibuk || undefined}>
                    <button type="button" className="psk-more__h" onClick={() => setLihatBlok((v) => !v)}>
                        <span className="psk-more__t">{t('prostudio.cfg.blokTitle')}</span>
                        <span className="psk-more__d">
                            {blokSendiri.length
                                ? t('prostudio.cfg.blokCount', { n: blokSendiri.length })
                                : t('prostudio.cfg.blokHint')}
                        </span>
                        <span className="psk-more__c"><ChevronRight size={15} strokeWidth={1.9} /></span>
                    </button>
                    {lihatBlok && (
                        <div className="psk-more__b">
                            <p className="psk-h__d" style={{ marginTop: 0 }}>{t('prostudio.cfg.blokAjar')}</p>

                            {/* ⛔ LALUAN "taip labelnya sendiri" DIBUANG (arahan user
                                   2026-08-21). Satu laluan sahaja tinggal: serlah teks
                                   di dalam jawapan sebenar. Sebabnya kukuh — label yang
                                   DITAIP hanya berpadan kalau ia sama huruf demi huruf
                                   dengan yang ada dalam jawapan, dan satu ruang atau
                                   titik bertindih yang tersasar menghasilkan blok yang
                                   kelihatan betul tetapi tidak pernah berpadan. Menyerlah
                                   daripada jawapan sendiri tidak boleh tersasar begitu.
                                   ⛔ Jangan pulangkan kotak taip itu tanpa membaca nota ini. */}

                            {/* Serlah teks dalam jawapan sebenar — SATU-SATUNYA laluan. */}
                            {jawapanPenuh ? (
                                <div>
                                    <Lbl>{t('prostudio.cfg.blokPilihLabel')}</Lbl>
                                    {/* Serlahan menunjukkan DI MANA setiap padanan berada.
                                        Ia bukan hiasan: kiraan seperti "6×" pada tiga set
                                        bermakna label itu turut muncul di tempat yang bukan
                                        tajuk (contoh sebenar: baris dialog "0-2s Hook:"),
                                        dan itu hanya kelihatan bila kedudukannya ditunjuk. */}
                                    <pre ref={rawRef} className="psk-raw psk-raw--pilih">
                                        {potongSerlah(jawapanPenuh, penandaBlok).map((k) => (k.serlah
                                            ? <mark key={k.kunci} className="psk-mark">{k.teks}</mark>
                                            : <span key={k.kunci}>{k.teks}</span>))}
                                    </pre>
                                    <div className="psk-model">
                                        <Btn variant="ghost" size="sm" icon={<Plus size={13} strokeWidth={2} />}
                                            /* ⚠️⚠️ PILIHAN DIRAKAM PADA mousedown, BUKAN pada click.
                                               Menekan butang dengan TETIKUS memindahkan fokus dan
                                               membuang serlahan pada teks — jadi menjelang onClick
                                               berjalan, `getSelection()` sudah kosong dan butang
                                               nampak seperti mati (aduan user 2026-08-20).
                                               `preventDefault` pada mousedown menghalang perpindahan
                                               fokus itu, jadi serlahan kekal.
                                               ⛔ Pepijat ini TIDAK dapat ditangkap oleh ujian yang
                                               memanggil .click() daripada kod — di situ tiada
                                               mousedown, jadi serlahan tidak pernah hilang. */
                                            onMouseDown={(e) => {
                                                const sel = window.getSelection?.();
                                                const dalam = sel && sel.rangeCount > 0 && rawRef.current
                                                    && rawRef.current.contains(sel.getRangeAt(0).commonAncestorContainer);
                                                pilihanRef.current = (dalam && sel.toString()) ? sel.toString() : '';
                                                e.preventDefault();
                                            }}
                                            onClick={() => {
                                                const teks = pilihanRef.current;
                                                const sebab = onTambahBlok(teks);
                                                setBlokPesan(sebab === 'ok' ? '' : (sebab || 'kosong'));
                                                if (sebab === 'ok') {
                                                    pilihanRef.current = '';
                                                    window.getSelection?.()?.removeAllRanges();
                                                }
                                            }}>
                                            {t('prostudio.cfg.blokDaripadaPilihan')}
                                        </Btn>
                                        <span className="psk-model__h">{t('prostudio.cfg.blokPilihHint')}</span>
                                    </div>
                                    {blokPesan && (
                                        <p className="psk-h__d psk-h__d--amaran">
                                            {t(`prostudio.cfg.blokPesan.${blokPesan}`)}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                /* Tiada jawapan pada skrin = tiada apa untuk diserlah.
                                   Selepas laluan taip dibuang, laci ini akan kelihatan
                                   rosak tanpa ayat ini — pelanggan membaca "tiada
                                   kawalan" sebagai pepijat, bukan sebagai syarat. */
                                <p className="psk-h__d psk-h__d--amaran" style={{ marginTop: 0 }}>
                                    {t('prostudio.cfg.blokPerluJawapan')}
                                </p>
                            )}

                            {/* 3) Senarai blok sendiri + bukti ia betul-betul berpadan. */}
                            {blokSendiri.length > 0 && (
                                <div className="psk-list">
                                    {blokSendiri.map((b) => (
                                        <div key={b.teks} className="psk-item">
                                            <div className="psk-blok">
                                                <div className="psk-blok__c">
                                                    {/* Nama blok = teks yang pelanggan BETUL-BETUL pilih,
                                                        bukan bentuk slugnya (arahan user 2026-08-20:
                                                        serlah 'HOOK: "' patut memapar 'HOOK: "', bukan
                                                        @hook). Slug kekal wujud — ia kod yang disisip ke
                                                        dalam medan — tetapi ia bukan lagi identiti yang
                                                        pelanggan lihat di sini. */}
                                                    <span className="psk-tok">{b.teks}</span>
                                                    <span className={`psk-blok__k${b.kira ? '' : b.kira === null ? ' psk-blok__k--tak-tahu' : ' psk-blok__k--kosong'}`}>
                                                        {b.kira
                                                            ? t('prostudio.cfg.blokJumpa', { n: b.kira })
                                                            : b.kira === null
                                                                ? t('prostudio.cfg.blokTakSemak')
                                                                : t('prostudio.cfg.blokTiada')}
                                                    </span>
                                                    {b.nilai && <span className="psk-blok__v">{b.nilai}</span>}
                                                </div>
                                                <Btn variant="quiet" size="sm" onClick={() => onBuangBlok(b.teks)}>
                                                    {t('prostudio.cfg.blokBuang')}
                                                </Btn>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className={`psk-list${pusinganSibuk ? ' psk-sibuk' : ''}`} aria-busy={pusinganSibuk || undefined}>
                {kumpulan.map((g) => (
                    <div key={g.id}>
                        {/* Tajuk kumpulan PILIHAN. Satu senarai rata (tiada tajuk)
                            ialah keadaan biasa; kumpulan hanya berbaloi bila ia
                            memberitahu pelanggan sesuatu yang boleh dia gunakan. */}
                        {g.tajuk ? <div className="psk-grp">{g.tajuk}</div> : null}
                        {g.medan.map((f) => {
                            const nilai = padanan[f.id] || '';
                            const cocok = berpadan(nilai, sumber);
                            /* ⛔ Tanda hijau HANYA bila tiada amaran blok juga.
                               `cocok` sahaja bermakna "baris ini menuding kepada
                               satu blok" — ia tidak menjamin blok itu ada dalam
                               jawapan, apatah lagi cukup kali. Tanda hijau di
                               sebelah ayat amaran membuat baris itu membaca dua
                               benda bertentangan serentak. */
                            const elok = cocok && !f.nota;
                            const dibuka = buka === f.id;
                            const Ikon = IKON[f.ikon] || Type;
                            const serpih = pecah(nilai);
                            return (
                                <div
                                    key={f.id}
                                    className={`psk-item${dibuka ? ' psk-item--buka' : ''}${elok ? '' : ' psk-item--kurang'}`}
                                >
                                    <button
                                        type="button"
                                        className="psk-row"
                                        onClick={() => setBuka(dibuka ? null : f.id)}
                                        title={dibuka ? t('prostudio.cfg.done') : t('prostudio.cfg.edit')}
                                    >
                                        <span className="psk-row__i"><Ikon size={15} strokeWidth={1.9} /></span>
                                        <span className="psk-row__n">{f.nama}</span>
                                        {/* Ringkasan nilai disorok semasa baris dibuka — kotak
                                            teks di bawah sudah memaparkan perkara yang sama, dan
                                            dua salinan pada skrin serentak itulah jenis pertindihan
                                            yang menjadikan langkah ini serabut pada mulanya. */}
                                        <span className="psk-row__v">
                                            {dibuka ? null : serpih.length
                                                ? serpih.map((p) => (p.jenis === 'blok'
                                                    ? <span key={p.kunci} className="psk-tok"
                                                        title={sumberLabel[p.teks.slice(1)] || p.teks}>
                                                        {namaBlok(p.teks.slice(1), sumberLabel)}</span>
                                                    : <span key={p.kunci}>{p.teks}</span>))
                                                : <span className="psk-kosong">{t('prostudio.cfg.rowEmpty')}</span>}
                                        </span>
                                        <span className="psk-row__s">
                                            {elok
                                                ? <Check size={16} strokeWidth={2.2} style={{ color: 'var(--s-ok)' }} />
                                                : <AlertTriangle size={15} strokeWidth={2} style={{ color: 'var(--s-warn)' }} />}
                                        </span>
                                        <span className="psk-row__c"><ChevronDown size={15} strokeWidth={1.9} /></span>
                                    </button>

                                    {/* ⭐ Amaran BILANGAN KEMUNCULAN (2026-08-26).
                                        Skrin dahulu senyap tentang perkara ini: satu blok
                                        boleh "berpadan" tetapi tidak muncul CUKUP KALI
                                        untuk mengisi semua set — dan set yang berbaki
                                        menerima teks milik set lain, atau langsung kosong.
                                        Tanda hijau sahaja tidak pernah mendedahkannya. */}
                                    {f.nota && (
                                        <p className="psk-nota">
                                            {f.nota.jenis === 'kosong'
                                                ? t('prostudio.cfg.notaKosong', { blok: f.nota.blok })
                                                : t('prostudio.cfg.notaTakCukup', { blok: f.nota.blok, n: f.nota.kira, perlu: f.nota.perlu })}
                                        </p>
                                    )}

                                    {dibuka && (
                                        <div className="psk-edit">
                                            <textarea
                                                ref={taRef}
                                                className="psk-edit__ta"
                                                value={nilai}
                                                onChange={(e) => onPadanan(f.id, e.target.value)}
                                                onSelect={simpanKursor}
                                                onBlur={simpanKursor}
                                                placeholder={t('prostudio.cfg.composerPh')}
                                            />
                                            <div className="psk-pal">
                                                <span className="psk-pal__l">{t('prostudio.cfg.insert')}</span>
                                                {/* title = teks blok sebenar. Bila dua blok serupa,
                                                    kod kedua dinomborkan (@hook2) dan nombor itu
                                                    sendiri tidak memberitahu blok yang mana. */}
                                                {sumber.map((s) => (
                                                    <button key={s} type="button" className="psk-pal__b"
                                                        title={sumberLabel[s] || ('@' + s)}
                                                        onClick={() => selitBlok(f.id, s)}>{namaBlok(s, sumberLabel)}</button>
                                                ))}
                                            </div>
                                            <div className="psk-edit__f">
                                                <span className="psk-edit__h">{t('prostudio.cfg.editHint')}</span>
                                                <Btn variant="quiet" size="sm" onClick={() => setBuka(null)}>
                                                    {t('prostudio.cfg.done')}
                                                </Btn>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* Laci tetapan lanjutan — kini SATU kawalan sahaja: pusingan jawapan mana
   yang menjadi output. Model Gemini + teks mentah keluar dari sini pada
   2026-08-21 (arahan user) dan naik ke bawah tajuk seksyen padanan.
   Ia tetap komponen berasingan supaya laci itu ada satu takrifan sahaja.
   Kedudukan (2026-09-13): terus di bawah seksyen model Gemini — dan tempat yang
   sama untuk ChatGPT — BUKAN lagi di hujung skrin. Lihat nota pada panggilannya. */
function Lanjutan({ t, buka, setBuka, pusingan = [], pusinganPilih = 0, onPusingan, pusinganSibuk = false, saatPadan = 0, siap }) {
    /* Pilihan yang BARU ditekan, dipapar sepanjang padanan semula. Halaman hidup
       hanya menukar `pusinganPilih` SELEPAS padanan siap — tanpa ini kotak
       memapar pilihan LAMA di sebelah tanda sibuk, dan pelanggan menyangka
       pilihannya tidak diterima. Bila tidak sibuk (siap / gagal), nilai sebenar
       daripada pemanggil sentiasa menang. */
    const [tertunda, setTertunda] = useState(null);
    /* Pusingan hanya bermakna bila agent benar-benar menjawab lebih sekali —
       satu pusingan bermaksud tiada pilihan untuk dibuat. */
    if (!siap || pusingan.length <= 1) return null;
    const nilaiPapar = (pusinganSibuk && tertunda !== null) ? tertunda : pusinganPilih;

    return (
        <div className={`psk-more${buka ? ' psk-more--buka' : ''}`}>
            <button type="button" className="psk-more__h" onClick={() => setBuka((v) => !v)}>
                <span className="psk-more__t">{t('prostudio.cfg.adv')}</span>
                <span className="psk-more__d">{t('prostudio.cfg.advHint')}</span>
                <span className="psk-more__c"><ChevronRight size={15} strokeWidth={1.9} /></span>
            </button>
            {buka && (
                <div className="psk-more__b">
                    <div>
                        <Lbl>{t('prostudio.agentsec.outputFrom')}</Lbl>
                        <Sel
                            value={String(nilaiPapar)} disabled={pusinganSibuk}
                            onChange={(e) => {
                                const v = Number(e.target.value);
                                setTertunda(v);
                                onPusingan?.(v);
                            }}
                        >
                            {pusingan.map((p) => <option key={p.nilai} value={String(p.nilai)}>{p.label}</option>)}
                        </Sel>
                        {/* Tanda sibuk DI TEMPAT pelanggan menekan — dulu hanya teks
                            kelabu kecil yang mudah terlepas pandang (aduan user 2026-09-13). */}
                        {pusinganSibuk ? (
                            <div className="psk-sedang" role="status">
                                <Loader2 size={13} strokeWidth={2.2} className="psk-spin" />
                                <span>{t('prostudio.agentsec.rematching')}</span>
                                <span className="psk-sedang__t">{saatPadan}s</span>
                            </div>
                        ) : (
                            <p className="psk-h__d">{t('prostudio.agentsec.outputFromHint')}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
